import { AnalyticsDayPoint } from './analyticsDay.types';

/**
 * 일별 값의 분포 요약.
 *
 * ⚠️ 대상은 **값이 있는 날(value > 0)만**이다.
 * 발인이 없던 날·주문이 없던 날까지 분모에 넣으면 평균·중앙값이 0쪽으로 끌려가
 * "장사가 되는 날은 보통 얼마나 나오나"를 못 본다. (하루 평균 칩과 같은 기준)
 * 조회 구간 밖의 날은 애초에 days에 없으므로 자동으로 빠진다.
 */
export interface DistributionStats {
  /** 값이 있었던 날 수 */
  count: number;
  total: number;
  mean: number;
  /** 중앙값 — 하루 몰린 날이 평균을 끌어올릴 때 평균과 벌어진다 */
  median: number;
  min: AnalyticsDayPoint;
  max: AnalyticsDayPoint;
  /** 모표준편차 (표본이 아니라 그 기간 전체를 보는 것이므로 n으로 나눈다) */
  stdDev: number;
  /** 변동계수 = stdDev / mean. 단위가 달라도 비교되는 "들쭉날쭉한 정도". mean이 0이면 null */
  cv: number | null;
}

/** 정렬된 숫자 배열의 중앙값 */
const medianOfSorted = (sorted: number[]): number => {
  const n = sorted.length;
  if (n === 0) return 0;
  const mid = Math.floor(n / 2);
  if (n % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
};

/** 값이 있는 날이 하나도 없으면 null */
export const describeDistribution = (
  days: AnalyticsDayPoint[],
): DistributionStats | null => {
  const active = days.filter((day) => day.value > 0);
  const first = active[0];
  if (!first) return null;

  let total = 0;
  let min = first;
  let max = first;
  active.forEach((day) => {
    total += day.value;
    if (day.value < min.value) min = day;
    //* 동점이면 먼저 나온 날(=이른 날)을 유지한다
    if (day.value > max.value) max = day;
  });

  const count = active.length;
  const mean = total / count;
  const variance =
    active.reduce((sum, day) => sum + (day.value - mean) ** 2, 0) / count;
  const stdDev = Math.sqrt(variance);

  const sorted = active.map((day) => day.value).sort((a, b) => a - b);

  return {
    count,
    total,
    mean,
    median: medianOfSorted(sorted),
    min,
    max,
    stdDev,
    cv: mean > 0 ? stdDev / mean : null,
  };
};
